"use client";

import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Image } from "@/components/primitives";

export type ResolvedNassifSlide = {
  id: string;
  name: string;
  poster: string;
  youtubeId: string;
};

type NassifVideoCarouselProps = {
  slides: ReadonlyArray<ResolvedNassifSlide>;
  /** Slide centered on first paint (default: the 2nd, so one peeks on the left). */
  initialCenterIndex?: number;
};

/** Width of the left/right arrow-cursor hover strips. */
const getNavZoneWidth = () => Math.min(window.innerWidth * 0.2, 180);

/**
 * Landscape video-testimonial carousel below the case study. Reuses the hero
 * carousel's full-bleed viewport/track scaffolding (`.reviews-feature-carousel*`
 * + `.reviews-nassif-carousel` overrides): horizontal scroll-snap, mouse
 * drag-to-scroll, and arrow-cursor nav zones on BOTH edges. Adds dot pagination
 * and centers on the 2nd slide on load. Tapping a card plays its clip inline.
 * No hover preview.
 */
const NassifVideoCarousel = ({ slides, initialCenterIndex = 1 }: NassifVideoCarouselProps) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const dragRef = useRef<{ startX: number; startScroll: number } | null>(null);
  const didDragRef = useRef(false);
  // A card clicked while off-center: centered first, then played once it lands.
  const pendingPlayRef = useRef<string | null>(null);
  // True once the viewer has played any video — from then on, moving to a new
  // centered card stops the old one and auto-plays the newly-centered one.
  const autoPlayRef = useRef(false);
  const playingIdRef = useRef<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [activeIndex, setActiveIndex] = useState(initialCenterIndex);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [navSide, setNavSide] = useState<"left" | "right" | null>(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  playingIdRef.current = playingId;

  const centerSlide = useCallback((index: number, behavior: ScrollBehavior = "smooth") => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const slide = track.querySelectorAll<HTMLElement>("li[data-slide]")[index];
    if (!slide) return;
    const viewportRect = viewport.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const delta = slideRect.left - viewportRect.left - (viewport.clientWidth - slideRect.width) / 2;
    viewport.scrollBy({ left: delta, behavior });
  }, []);

  // Index of the slide whose center is nearest the viewport center.
  const nearestIndex = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return 0;
    const viewportRect = viewport.getBoundingClientRect();
    const centerX = viewportRect.left + viewportRect.width / 2;
    let best = 0;
    let bestDelta = Infinity;
    track.querySelectorAll<HTMLElement>("li[data-slide]").forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const delta = Math.abs(rect.left + rect.width / 2 - centerX);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = index;
      }
    });
    return best;
  }, []);

  // Center the initial slide before paint so one card already peeks on the left.
  useEffect(() => {
    centerSlide(initialCenterIndex, "auto");
  }, [centerSlide, initialCenterIndex]);

  // Live: track the nearest-center slide (drives the active dot + sizing).
  // On settle (scroll/drag stops): snap that card to dead-center so one is
  // always centered on release, then play any card clicked to be centered.
  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const centerDelta = (index: number) => {
      const slide = track.querySelectorAll<HTMLElement>("li[data-slide]")[index];
      if (!slide) return 0;
      const viewportRect = viewport.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      return slideRect.left - viewportRect.left - (viewport.clientWidth - slideRect.width) / 2;
    };

    let frame: number | null = null;
    let settle: number | null = null;
    const onScroll = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setActiveIndex(nearestIndex()));

      if (settle !== null) window.clearTimeout(settle);
      settle = window.setTimeout(() => {
        if (dragRef.current) return; // still dragging
        const best = nearestIndex();
        setActiveIndex(best);
        const delta = centerDelta(best);
        if (Math.abs(delta) > 2) {
          viewport.scrollBy({ left: delta, behavior: "smooth" }); // snap to grid; re-settles when it lands
          return;
        }
        const bestId = slides[best]?.id ?? null;
        if (pendingPlayRef.current) {
          const pendingIndex = slides.findIndex((slide) => slide.id === pendingPlayRef.current);
          if (pendingIndex === best) {
            setPlayingId(pendingPlayRef.current);
            autoPlayRef.current = true;
            pendingPlayRef.current = null;
          }
        } else if (autoPlayRef.current && bestId && bestId !== playingIdRef.current) {
          // Already watching: the centered card changed, so stop the old video
          // and auto-play the newly-centered one.
          setPlayingId(bestId);
        }
      }, 150);
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      viewport.removeEventListener("scroll", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
      if (settle !== null) window.clearTimeout(settle);
    };
  }, [slides, nearestIndex]);

  // Mouse drag-to-scroll via window listeners (keeps the trailing click intact
  // so a clean click still opens the video — same rationale as the hero).
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLUListElement>) => {
      didDragRef.current = false;
      if (event.button !== 0 || event.pointerType !== "mouse") return;
      const viewport = viewportRef.current;
      if (!viewport) return;

      dragRef.current = { startX: event.clientX, startScroll: viewport.scrollLeft };

      const onMove = (moveEvent: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const deltaX = moveEvent.clientX - drag.startX;
        if (Math.abs(deltaX) > 6 && !didDragRef.current) {
          didDragRef.current = true;
          setIsDragging(true);
          setNavSide(null);
        }
        if (didDragRef.current) viewport.scrollLeft = drag.startScroll - deltaX;
      };
      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setIsDragging(false);
        // Snap to the nearest card on release. Don't rely on a trailing scroll
        // event to do it — none fires if the pointer was held still before
        // release, which would leave the track resting between two cards.
        if (didDragRef.current) centerSlide(nearestIndex());
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [centerSlide, nearestIndex],
  );

  const handleTrackClick = useCallback(
    (event: React.MouseEvent<HTMLUListElement>) => {
      if (didDragRef.current) return;
      const slideEl = (event.target as HTMLElement).closest<HTMLElement>("li[data-slide]");
      const id = slideEl?.dataset.slide;
      if (!id) return;
      const index = slides.findIndex((slide) => slide.id === id);
      if (index < 0) return;
      if (index === activeIndex) {
        setPlayingId(id); // already centered → play in place
        autoPlayRef.current = true;
      } else {
        // Center it first; the scroll-settle handler plays it once it lands.
        pendingPlayRef.current = id;
        centerSlide(index);
      }
    },
    [slides, activeIndex, centerSlide],
  );

  const go = useCallback(
    (direction: 1 | -1) => {
      const next = Math.min(Math.max(nearestIndex() + direction, 0), slides.length - 1);
      centerSlide(next);
    },
    [nearestIndex, slides.length, centerSlide],
  );

  // Arrow-cursor nav zones — mouse-only affordance, skipped on touch/coarse.
  // The vertical band is the CARD itself (not the container, which also spans
  // the dots + shadow bleed), so the arrow only appears over the video row. The
  // current index is read live so the arrow never sticks on stale state.
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

    const onMove = (event: PointerEvent) => {
      if (isDragging || dragRef.current || playingId) {
        setNavSide(null);
        return;
      }
      const card = trackRef.current?.querySelector<HTMLElement>("li[data-slide]");
      if (!card) return;
      const bounds = card.getBoundingClientRect();
      if (event.clientY < bounds.top || event.clientY > bounds.bottom) {
        setNavSide(null);
        return;
      }
      const zone = getNavZoneWidth();
      const current = nearestIndex();
      if (event.clientX <= zone && current > 0) {
        setNavSide("left");
        setCursorPos({ x: event.clientX, y: event.clientY });
      } else if (event.clientX >= window.innerWidth - zone && current < slides.length - 1) {
        setNavSide("right");
        setCursorPos({ x: event.clientX, y: event.clientY });
      } else {
        setNavSide(null);
      }
    };

    window.addEventListener("pointermove", onMove);
    return () => window.removeEventListener("pointermove", onMove);
  }, [isDragging, playingId, slides.length, nearestIndex]);

  return (
    <div
      className={`reviews-feature-carousel reviews-nassif-carousel relative ${
        navSide && !isDragging ? "reviews-feature-carousel--nav-active" : ""
      }`}
    >
      <div className="reviews-feature-carousel__viewport" ref={viewportRef}>
        <ul
          className={`reviews-feature-carousel__track m-0 flex list-none gap-3 p-0 ${
            isDragging ? "reviews-feature-carousel__track--dragging" : ""
          }`}
          onClick={handleTrackClick}
          onDragStart={(event) => event.preventDefault()}
          onPointerDown={handlePointerDown}
          ref={trackRef}
        >
          {slides.map((slide, slideIndex) => {
            const isPlaying = playingId === slide.id;
            return (
              <li
                className={`reviews-feature-carousel__slide shrink-0 snap-center ${
                  slideIndex === activeIndex ? "is-center" : "is-side"
                }`}
                data-slide={slide.id}
                key={slide.id}
              >
                <div
                  className={`reviews-feature-carousel__slide-surface ${
                    isPlaying ? "" : "cursor-pointer"
                  }`}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    if (!isPlaying) setPlayingId(slide.id);
                  }}
                  role={isPlaying ? undefined : "button"}
                  tabIndex={isPlaying ? undefined : 0}
                >
                  <figure className="reviews-feature-tile reviews-nassif-tile relative m-0 overflow-hidden">
                    {/* Thumbnail bakes in its own quote + attribution text, so
                        serve the original webp uncompressed — re-encoding softens
                        the baked text. Already an optimized ~80KB 1920x1080 file. */}
                    <Image
                      alt={slide.name}
                      className="reviews-nassif-tile__image object-cover object-center"
                      draggable={false}
                      fill
                      sizes="680px"
                      src={slide.poster}
                      unoptimized
                    />
                    {isPlaying ? (
                      <iframe
                        allow="autoplay; encrypted-media; picture-in-picture; web-share; fullscreen"
                        allowFullScreen
                        className="bg-mocha absolute inset-0 z-30 h-full w-full"
                        src={`https://www.youtube-nocookie.com/embed/${slide.youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                        title={slide.name}
                      />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="reviews-nassif-tile__play bg-champagne-white text-mocha absolute top-1/2 left-1/2 z-20 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                      >
                        <Play className="ml-1 fill-current" strokeWidth={0} />
                      </span>
                    )}
                  </figure>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="reviews-nassif-carousel__dots" aria-label="Video testimonials" role="tablist">
        {slides.map((slide, index) => (
          <button
            aria-label={`Go to video ${index + 1}`}
            aria-selected={index === activeIndex}
            className={`reviews-nassif-carousel__dot ${index === activeIndex ? "is-active" : ""}`}
            key={slide.id}
            onClick={() => centerSlide(index)}
            role="tab"
            type="button"
          />
        ))}
      </div>

      <div
        aria-hidden="true"
        className="reviews-nassif-carousel__nav-zone reviews-nassif-carousel__nav-zone--left"
        onClick={() => go(-1)}
        style={{ pointerEvents: navSide === "left" && !isDragging ? "auto" : "none" }}
      />
      <div
        aria-hidden="true"
        className="reviews-nassif-carousel__nav-zone reviews-nassif-carousel__nav-zone--right"
        onClick={() => go(1)}
        style={{ pointerEvents: navSide === "right" && !isDragging ? "auto" : "none" }}
      />
      <span
        aria-hidden="true"
        className={`reviews-feature-carousel__cursor bg-champagne-white text-mocha pointer-events-none fixed z-[80] flex size-14 items-center justify-center rounded-full shadow-[0_10px_30px_rgba(40,29,26,0.18)] transition-opacity duration-200 ${
          navSide && !isDragging ? "opacity-100" : "opacity-0"
        }`}
        style={{ left: cursorPos.x, top: cursorPos.y, transform: "translate(-50%, -50%)" }}
      >
        {navSide === "left" ? (
          <ArrowLeft className="size-5" strokeWidth={1.75} />
        ) : (
          <ArrowRight className="size-5" strokeWidth={1.75} />
        )}
      </span>
    </div>
  );
};

export default NassifVideoCarousel;
